import type { ReactElement } from "react"
import { Fragment, useContext, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import { Grid } from "@mui/material"
import { Button, InputText, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import { v4 as uuidv4 } from "uuid"

import { MonacoTextEditor } from "../../../components/data-display/MonacoTextEditor"
import { useResizeObserver } from "../../../hooks/useResizeObserver"
import type { RootState } from "../../../store/ReduxStore"
import { Deployment } from "../../../types/generated/api/Deployment"
import type { Model } from "../../../types/generated/api/Model"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import { convertNestedObjectsToFeatureStrings } from "../../../utils/modelDetailsHelpers"
import { WorkflowCanvasContext } from "../../Workflows"
import { SMALL_ICON_SIZE } from "../Constants"
import { Context } from "../Context"
import type { EditorType, PredictionOutputProps, Schema } from "../Interfaces"
import { EDITOR_TYPES } from "../Interfaces"
import {
  convertJsonToTableSchema,
  convertNestedFeatureStringsToObjects,
  dummyValueBasedOnType,
  typeCastBasedOnType,
} from "../helpers"
import { TabularEditor } from "./editors/TabularEditor"

import styles from "./SinglePredictionCreation.module.scss"

/**
 * Component to display prediction results in a formatted JSON viewer
 * @param data - The prediction result data to display
 * @returns {ReactElement} A formatted display of the prediction output
 */
const PredictionOutput = ({ data, isExpanded, setIsExpanded, isTestData }: PredictionOutputProps): ReactElement => {
  const { t } = useTranslation("predictions", {
    keyPrefix: "singlePredictionCreation.predictionOutput",
  })

  return (
    <Grid container display="flex" flexDirection="column" minHeight={isExpanded ? "50%" : undefined}>
      {/* Header section for prediction output */}
      <Grid item className={`${styles.outputHeader} ${!isExpanded && styles.fullRounded}`}>
        <Button
          size="small"
          onClick={() => {
            setIsExpanded((prev) => !prev)
          }}
        >
          {isExpanded ? (
            <KeyboardArrowRightIcon style={{ ...SMALL_ICON_SIZE }} />
          ) : (
            <KeyboardArrowDownIcon style={{ ...SMALL_ICON_SIZE }} />
          )}
        </Button>
        <Typography variant="a">{isTestData ? t("title.test") : t("title.prediction")}</Typography>
        <Typography variant="a" variantColor={2}>
          ({"output_label" in data ? data.output_label : data.joint_output})
        </Typography>
      </Grid>

      {/* Monaco editor to display formatted JSON output */}
      {isExpanded && (
        <Grid item flex={1} className={styles.monaco}>
          <MonacoTextEditor
            value={JSON.stringify(data, null, "\t")}
            readOnly
            language="json"
            borderRadius={"0 0 8px 8px"}
          />
        </Grid>
      )}
    </Grid>
  )
}

/**
 * SinglePredictionCreation component allows users to create and send predictions and Testing
 * using either a table-based form or JSON editor interface.
 *
 * Features:
 * - Dual editor modes (table/JSON)
 * - Dynamic schema validation
 * - Real-time JSON validation
 * - Pagination for table view
 * - Search functionality
 * - Support for multiple data types
 *
 * @returns {ReactElement} A form interface for creating predictions and Testing
 */
export function SinglePredictionCreation(): ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const { workflow } = useContext(WorkflowCanvasContext)
  const { sendPredictionMutation, sendWorkFlowPredictionTestMutation, sendModelPredictionTestMutation, isTestModal } =
    useContext(Context)

  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const currentModel = useSelector((state: RootState) => state.models.currentModel) as Model

  const engineSchema =
    currentProject?.category === Deployment.category.WORKFLOW
      ? workflow?.active_version.schema.features
      : currentModel?.schema.features

  const { t } = useTranslation("predictions", {
    keyPrefix: "singlePredictionCreation",
  })

  // Track current editor mode (table or JSON)
  const [editorType, setEditorType] = useState<EditorType>(EDITOR_TYPES.TABLE)

  // Store JSON string representation of the data
  const [json, setJson] = useState<string | undefined>("{}")
  // Store structured schema with features and their values
  const [schema, setSchema] = useState<Schema>(
    engineSchema?.reduce(
      (acc, feature) => ({
        ...acc,
        [feature.name]: {
          ...feature,
          value: dummyValueBasedOnType(feature.type ?? WorkflowInputFeature.type.TEXT),
        },
      }),
      {},
    ) ?? {},
  )

  // Pagination and search state for table view
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string | undefined>(undefined)

  // Track JSON validity for error handling
  const [isJsonValid, setIsJsonValid] = useState<boolean>(true)

  const [isOutputExpanded, setIsOutputExpanded] = useState<boolean>(false)

  const [tableRef, { height }] = useResizeObserver()

  const rowsPerPage = Math.floor(((height ?? 0) - 37 - 5) / 41)

  /**
   * Switches between table and JSON editor modes
   * @param newEditor - The editor type to switch to
   */
  const handleEditorSwitch = (newEditor: EditorType): void => {
    if (newEditor === EDITOR_TYPES.JSON) {
      // Convert table data to JSON format
      const newData: Record<string, unknown> = {}
      for (const feature of Object.values(schema)) {
        // falling back to null since JSON.stringify doesn't allow undefined
        // https://stackoverflow.com/a/32179927
        newData[feature.name] = typeCastBasedOnType(feature.value, feature.type) ?? null
      }

      setJson(JSON.stringify(newData, null, "\t"))
      setEditorType(newEditor)
    } else if (newEditor === EDITOR_TYPES.TABLE) {
      try {
        // Parse JSON and convert to table format
        const parsedJson = convertNestedObjectsToFeatureStrings({
          obj: JSON.parse(json as string) ?? {},
        })
        const newSchema = convertJsonToTableSchema(parsedJson, engineSchema)
        setIsJsonValid(true)
        setEditorType(newEditor)
        setSchema(newSchema)
      } catch {
        setIsJsonValid(false)
      }
    }
  }

  /**
   * Builds the prediction request based on the current editor type
   * @returns {Record<string, unknown>} The built prediction request object
   * @throws Error if JSON is invalid
   */
  const buildPredictionRequest = (): Record<string, unknown> => {
    if (editorType === EDITOR_TYPES.JSON) {
      return JSON.parse(json as string)
    }

    // Build request from table schema with respect to types
    const predictionRequest: Record<string, unknown> = {}
    for (const item of Object.values(schema)) {
      predictionRequest[item.name] = typeCastBasedOnType(item.value, item.type)
    }
    return predictionRequest
  }

  /**
   * Handles the submission of prediction data to the API
   */
  const handleSendingPrediction = async (): Promise<void> => {
    try {
      const predictionRequest = buildPredictionRequest()
      if (isTestModal) {
        if (currentProject?.category === "workflow") {
          await sendWorkFlowPredictionTestMutation?.mutateAsync({
            workflowId: workflow?.uuid as string,
            predictionBody: predictionRequest,
          })
        }

        if (currentProject?.category === "model") {
          await sendModelPredictionTestMutation?.mutateAsync({
            modelId: currentModel?.uuid as string,
            predictionBody: predictionRequest,
          })
        }
      } else {
        await sendPredictionMutation?.mutateAsync({
          predictionBody: predictionRequest,
          projectId: projectId as string,
        })
      }
      setIsOutputExpanded(true)
    } catch (error) {
      setIsJsonValid(false)
      console.error("Failed to send prediction:", error)
    }
  }

  /**
   * Adds a new feature to the schema with default values
   */
  const handleAddNewFeature = (): void => {
    const key = uuidv4()
    const feat = {
      [key]: {
        name: "",
        type: WorkflowInputFeature.type.TEXT,
        id: key,
        value: "string",
        new: true,
      },
    }

    setSchema((prev) => {
      return {
        ...prev,
        ...feat,
      }
    })
  }

  const isPredictionLoading =
    sendPredictionMutation?.isLoading ||
    sendModelPredictionTestMutation?.isLoading ||
    sendWorkFlowPredictionTestMutation?.isLoading

  const predictionData =
    sendPredictionMutation?.data?.data ||
    sendModelPredictionTestMutation?.data?.data ||
    sendWorkFlowPredictionTestMutation?.data?.data

  return (
    <Grid container className={styles.root}>
      <Grid container className={styles.editorContainer}>
        <Grid item display={"flex"} justifyContent={"space-between"}>
          <Grid item>
            <InputText
              variant="filled"
              placeholder={t("searchPlaceholder")}
              hideDescription
              disabled={editorType === EDITOR_TYPES.JSON}
              value={search}
              type="text"
              handleChange={(e) => setSearch(e.target.value as string)}
            />
          </Grid>

          <Grid item display={"flex"} gap={1}>
            {editorType === EDITOR_TYPES.TABLE && (
              <Button variant="secondary" onClick={handleAddNewFeature}>
                {t("buttons.addFeature")}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() =>
                handleEditorSwitch(editorType === EDITOR_TYPES.TABLE ? EDITOR_TYPES.JSON : EDITOR_TYPES.TABLE)
              }
            >
              {(editorType === EDITOR_TYPES.TABLE
                ? t(`buttons.${EDITOR_TYPES.JSON}`)
                : t(`buttons.${EDITOR_TYPES.TABLE}`)
              ).toUpperCase()}
            </Button>
            <Button
              variant="primary"
              onClick={handleSendingPrediction}
              isLoading={isPredictionLoading}
              disabled={isPredictionLoading}
            >
              {isTestModal ? t("buttons.test") : t("buttons.predict")}
            </Button>
          </Grid>
        </Grid>

        <Grid item className={styles.editor} ref={tableRef} key={`output-${isOutputExpanded}-${rowsPerPage}`}>
          {editorType === EDITOR_TYPES.JSON ? (
            // Adding key to make the editor re-render when the output expands to fix overflow
            <Fragment>
              <Grid item className={styles.jsonEditorHeader}>
                <Typography variant={"a"}>{t("jsonEditor.title")}</Typography>

                {!isJsonValid && (
                  <Typography variant={"a"} color="negative" variantColor={2}>
                    {t("jsonEditor.invalidDataError")}
                  </Typography>
                )}
              </Grid>

              <Grid item display={"flex"} flex={1} className={styles.monaco}>
                <MonacoTextEditor
                  value={convertNestedFeatureStringsToObjects(json ?? "{}")}
                  onChange={(value) => {
                    const nestedJsonObject = convertNestedFeatureStringsToObjects(value as string)
                    setJson(nestedJsonObject)
                  }}
                  height="100%"
                  language={"json"}
                  borderRadius={"0 0 8px 8px"}
                />
              </Grid>
            </Fragment>
          ) : (
            <TabularEditor
              rowsPerPage={rowsPerPage}
              schema={schema}
              setSchema={setSchema}
              page={page - 1}
              search={search}
            />
          )}
        </Grid>

        {editorType === EDITOR_TYPES.TABLE && (
          <Pagination
            page={page}
            onPageChange={setPage}
            pageSize={rowsPerPage}
            onPageSizeChange={(): void => {
              throw new Error(t("paginationError"))
            }}
            totalCount={Object.keys(schema).length}
            variant={"complex"}
            hidePageSize
          />
        )}
      </Grid>

      {!!predictionData && (
        <PredictionOutput
          data={predictionData}
          isExpanded={isOutputExpanded}
          isTestData={isTestModal}
          setIsExpanded={setIsOutputExpanded}
        />
      )}
    </Grid>
  )
}
